import {
  HOLDOUT,
  sendExperimentTriggerThroughEventTracking,
  ExperimentVariants,
  DefaultVariants,
} from "@outschool/experiments-shared";
import {
  getBotRequestInfo,
  IntegrationCategory,
  usePageContext,
  useTrackEvent,
} from "@outschool/ui-analytics";
import jsonStringify from "json-stringify-safe";
import React from "react";

import { useServerExperimentContext } from "./ExperimentsServiceProvider";
import { useIsImpersonating } from "@outschool/ui-auth";

export default function useExperiment<
  Variants extends ExperimentVariants = DefaultVariants,
>({
  currentUserHasLoaded,
  experimentName,
  defaultVariant = HOLDOUT,
}: {
  currentUserHasLoaded: boolean;
  experimentName: string;
  defaultVariant?: string;
}) {
  const page = usePageContext();
  const isImpersonating = useIsImpersonating();

  const trackEvent = useTrackEvent(IntegrationCategory.Functional);

  const {
    eventSource,
    loggedInUserExperimentUid,
    loggedOutUserExperimentUid,
    experimentAssignments = {},
    experimentOverrides = {},
    shouldTrigger,
    isExperimentOverridesEnabled,
    isProduction,
  } = useServerExperimentContext();
  const hasOverride =
    isExperimentOverridesEnabled &&
    experimentOverrides.hasOwnProperty(experimentName);
  const hasExperiment = experimentAssignments.hasOwnProperty(experimentName);
  const variant: Variants = ((hasOverride
    ? experimentOverrides[experimentName]
    : experimentAssignments[experimentName]) ?? defaultVariant) as Variants;

  /**
    Trigger at the point the experience diverges, or earlier. Never after the
    assigned variant has rendered.

    Triggering after render ties the trigger rate to how fast each variant
    paints. People who leave mid-load are then dropped unevenly across arms,
    which surfaces as sample ratio mismatch and invalidates the results.

    For an experiment on what happens when a class listing is clicked, trigger
    on the click: that is where the experience diverges.

    For an experiment on the layout of a whole page, the variant is already
    decided on the server when the page is requested, so trigger there instead
    of from this hook.
  */
  const trigger = React.useCallback(
    ({
      additionalProperties,
    }: { additionalProperties?: Record<any, any> } = {}) => {
      if (
        currentUserHasLoaded &&
        ((isProduction && hasExperiment && !hasOverride) ||
          (!isProduction && (hasExperiment || hasOverride)))
      ) {
        if (
          shouldTrigger({
            experimentName,
            variantAssigned: variant,
            additionalProperties,
          })
        ) {
          const { cloudflare_bot_score } = getBotRequestInfo();
          sendExperimentTriggerThroughEventTracking(
            {
              experiment_name: experimentName,
              variant_assigned: variant,
              logged_out_user_experiment_uid: loggedOutUserExperimentUid,
              logged_in_user_experiment_uid: loggedInUserExperimentUid,
              page_category: page.pageCategory,
              page_name: page.pageName,
              user_id: loggedInUserExperimentUid,
              cloudflare_bot_score,
              additional_properties: jsonStringify({
                ...additionalProperties,
                ...(isImpersonating
                  ? { is_impersonating: isImpersonating }
                  : {}),
              }),
            },
            eventSource,
          ).then(() => {
            trackEvent("Experiment Triggered", {
              experiment_name: experimentName,
              experiment_variant: variant,
            });
          });
        }
      }
    },
    [
      currentUserHasLoaded,
      variant,
      experimentName,
      hasExperiment,
      hasOverride,
      loggedInUserExperimentUid,
      shouldTrigger,
      loggedOutUserExperimentUid,
      page,
      eventSource,
      isProduction,
      trackEvent,
      isImpersonating,
    ],
  );

  return {
    variant,
    trigger,
  };
}
